/**
 * useConfirm — Promise-basierter Bestätigungs-Hook (Sprint UI-D2a).
 *
 * Ersetzt den blockierenden nativen Browser-Confirm-Dialog. Call-Site
 * bleibt imperativ:
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   // im Handler:  if (!(await confirm({ title, message }))) return;
 *   // im JSX:      {confirmDialog}
 *
 * Concurrency-Contract: ein zweiter `confirm()` während ein Dialog offen ist
 * resolved das vorherige Promise mit `false` und ersetzt den Dialog.
 * Unmount-Contract: unmountet die Komponente mit offenem Dialog, wird das
 * pending Promise mit `false` resolved (kein Leak).
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ConfirmDialog, type ConfirmVariant } from './ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

interface ConfirmState {
  open: boolean;
  opts: ConfirmOptions | null;
}

export function useConfirm(): {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  confirmDialog: ReactNode;
} {
  const [state, setState] = useState<ConfirmState>({ open: false, opts: null });
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setState({ open: false, opts: null });
    resolve?.(value);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    // Concurrency-Contract: laufendes confirm() → false resolven, ersetzen.
    if (resolveRef.current) {
      const prev = resolveRef.current;
      resolveRef.current = null;
      prev(false);
    }
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  // Unmount-Contract: pending confirm() → false resolven (kein Leak).
  useEffect(
    () => () => {
      resolveRef.current?.(false);
      resolveRef.current = null;
    },
    [],
  );

  const confirmDialog = (
    <ConfirmDialog
      open={state.open}
      onClose={() => settle(false)}
      onConfirm={() => settle(true)}
      title={state.opts?.title ?? ''}
      message={state.opts?.message}
      confirmLabel={state.opts?.confirmLabel}
      cancelLabel={state.opts?.cancelLabel}
      variant={state.opts?.variant}
    />
  );

  return { confirm, confirmDialog };
}
